import type { SuretyBondFormType } from "@inrev/common";
import type { BondFormTemplate } from "@portal/api/bond-request/agent";
import { useMemo, useState } from "react";
import type { BondFormData } from "../../../types";
import { cn } from "../../lib/utils";
import { DOCUMENT_ACCEPT, Dropzone } from "../../patterns/dropzone";
import { Button, Icon, Text } from "../../primitives";
import { AttachmentTypeMultiSelect } from "../../ui/AttachmentTypeMultiSelect";
import { BondFormFileList } from "./bond-form-select/BondFormFileList";
import { BondFormLibraryModal } from "./bond-form-select/BondFormLibraryModal";
import { SelectedBondFormTemplateCard } from "./bond-form-select/SelectedBondFormTemplateCard";
import { useBondFormUpload } from "./bond-form-select/useBondFormUpload";

export type BondFormSelectInputProps = {
	value: BondFormData;
	onChange: (value: BondFormData) => void;
	onBlur?: () => void;
	templates: BondFormTemplate[];
	typeLabels: Partial<Record<SuretyBondFormType, string>>;
	onDownloadFile?: (fileId: string) => void;
	downloadUrl?: string;
	error?: boolean;
	allowDelete?: boolean;
	allowUpload?: boolean;
	allowLibrary?: boolean;
	compact?: boolean;
	id?: string;
	className?: string;
	testId?: string;
};

export const BondFormSelectInput = ({
	value: bondFormData,
	onChange,
	onBlur,
	templates,
	typeLabels,
	onDownloadFile,
	downloadUrl,
	error,
	allowDelete = true,
	allowUpload = true,
	allowLibrary = true,
	compact = false,
	id,
	className,
	testId = "bond-form-select",
}: BondFormSelectInputProps) => {
	const [showLibrary, setShowLibrary] = useState(false);

	const {
		allowedTypes,
		uploadType,
		filesUploaded,
		filesPendingUpload,
		fileIdsPendingDelete,
		pendingTypeSelection,
		handleDrop,
		handleDelete,
		handleTypeSelected,
		cancelTypeSelection,
		selectTemplate,
		deselectTemplate,
	} = useBondFormUpload({ value: bondFormData, onChange, onBlur, typeLabels });

	const selectedTemplate = useMemo(
		() => templates.find((template) => template.id === bondFormData.templateId),
		[bondFormData.templateId, templates],
	);

	const typeSelectGroupMap = useMemo(() => new Map([["Bond Form Type", typeLabels]]), [typeLabels]);

	const nothingSelected = !selectedTemplate && !filesUploaded;
	// Compact (or upload-only) empty state owns the dropzone so the section below does not duplicate it.
	const dropzoneOwnedByEmptyState = nothingSelected && (compact || !allowLibrary);

	const librarySelect = (
		<div className={cn("flex items-center", !compact && "space-x-[15px]")}>
			<Button
				className={cn("w-fit", !compact && "ml-[-5px]")}
				color="inverse"
				leadingIcon={<Icon type="book-open" size="mini" />}
				onClick={() => setShowLibrary(true)}
				data-testid="open-bond-form-library"
			>
				Select a Bond Form
			</Button>
			{allowUpload && (
				<Text
					type="caption"
					color="secondary"
					className={cn(compact && "-mr-6 flex-1 text-center")}
				>
					- OR -
				</Text>
			)}
		</div>
	);

	const dropzone = allowUpload && (
		<Dropzone
			accept={DOCUMENT_ACCEPT}
			onDrop={handleDrop}
			multiple
			size={filesUploaded ? "sm" : "md"}
			caption={compact ? null : undefined}
			testId="bond-form-dropzone"
		/>
	);

	const fileList = (
		<BondFormFileList
			files={bondFormData.upload.files}
			filesPendingUpload={filesPendingUpload}
			fileIdsPendingDelete={fileIdsPendingDelete}
			uploadType={uploadType}
			compact={compact}
			allowDelete={allowDelete}
			onDelete={handleDelete}
			onDownloadFile={onDownloadFile}
			downloadUrl={downloadUrl}
		/>
	);

	return (
		<>
			<div
				id={id}
				data-testid={testId}
				className={cn(
					compact ? "flex flex-col gap-[12px]" : "flex flex-col space-y-[30px]",
					className,
					error ? "relative rounded-md outline outline-offset-[10px] outline-danger" : undefined,
				)}
			>
				{nothingSelected &&
					(allowLibrary ? (
						compact ? (
							<div
								className="grid grid-cols-2 items-center gap-x-6"
								data-testid="bond-form-select-row"
							>
								{librarySelect}
								{dropzone}
							</div>
						) : (
							librarySelect
						)
					) : (
						dropzone
					))}
				{selectedTemplate && (
					<SelectedBondFormTemplateCard
						template={selectedTemplate}
						compact={compact}
						onRemove={allowDelete ? deselectTemplate : undefined}
					/>
				)}
				{!selectedTemplate &&
					!dropzoneOwnedByEmptyState &&
					(compact ? (
						<>
							{dropzone}
							{fileList}
						</>
					) : (
						<div className="w-full flex flex-col space-y-[15px]">
							{fileList}
							{dropzone}
						</div>
					))}
			</div>
			{allowLibrary && showLibrary && (
				<BondFormLibraryModal
					templates={templates}
					onSelect={(templateId) => {
						selectTemplate(templateId);
						setShowLibrary(false);
					}}
					onClose={() => setShowLibrary(false)}
				/>
			)}
			{pendingTypeSelection !== null && (
				<AttachmentTypeMultiSelect
					availableTypes={allowedTypes}
					groupMap={typeSelectGroupMap}
					initialTypes={[]}
					title={"Select Bond Form Type"}
					expandedByDefault={true}
					selectOne={true}
					onSave={handleTypeSelected}
					close={cancelTypeSelection}
				/>
			)}
		</>
	);
};
